// A boot step that failed after its retries (bootRetry.ts) shows this instead
// of leaving a black screen (2026-10-08, wraptest.nz): what went wrong in
// plain words, a Retry button, and it retries by itself when the browser
// says it is back online. Inline styles: it must render even when the app's
// own CSS bundle is what failed to load.

let current: HTMLElement | null = null;

export interface BootErrorOptions {
  title: string;
  message: string;
  /** Retry: resolve = recovered (the screen closes); reject = still failing */
  retry: () => Promise<void>;
  /** a second, quieter action (e.g. "Log out") */
  secondary?: { label: string; run: () => void };
}

export function hideBootError(): void {
  current?.remove();
  current = null;
}

export function offlineText(): string {
  return navigator.onLine
    ? "WRAP couldn't reach the server. If you've just changed network (Wi-Fi to mobile data), give it a moment."
    : "This phone is offline. WRAP will try again when the signal comes back.";
}

export function showBootError(opts: BootErrorOptions): void {
  hideBootError();
  const overlay = document.createElement("div");
  overlay.id = "boot-error";
  overlay.setAttribute("role", "alertdialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-labelledby", "boot-error-title");
  overlay.style.cssText =
    "position:fixed;inset:0;z-index:5000;display:flex;align-items:center;justify-content:center;padding:16px;" +
    "background:#0f1a24;color:#e2e8f0;font:15px/1.45 system-ui,sans-serif";
  const card = document.createElement("div");
  card.style.cssText = "max-width:420px;width:100%;background:#1a2a3a;border-radius:12px;padding:20px;box-shadow:0 8px 30px rgba(0,0,0,.4)";
  const h = document.createElement("h2");
  h.id = "boot-error-title";
  h.textContent = opts.title;
  h.style.cssText = "margin:0 0 8px;font-size:18px";
  const p = document.createElement("p");
  p.textContent = opts.message;
  p.style.cssText = "margin:0 0 6px";
  const status = document.createElement("p");
  status.setAttribute("aria-live", "polite");
  status.style.cssText = "margin:0 0 14px;color:#a0aec0;font-size:13px;min-height:1.2em";
  const row = document.createElement("div");
  row.style.cssText = "display:flex;gap:10px;flex-wrap:wrap";
  const retry = document.createElement("button");
  retry.type = "button";
  retry.textContent = "Retry";
  retry.style.cssText = "flex:1;min-width:120px;padding:12px;border:0;border-radius:8px;background:#4299e1;color:#fff;font-weight:600;font-size:16px";
  let busy = false;
  const run = async () => {
    if (busy) return;
    busy = true;
    retry.disabled = true;
    status.textContent = "Trying again…";
    try {
      await opts.retry();
      if (current === overlay) hideBootError();
    } catch {
      status.textContent = navigator.onLine ? "Still can't reach WRAP. Try again in a moment." : "Still offline.";
    } finally {
      busy = false;
      retry.disabled = false;
    }
  };
  retry.addEventListener("click", () => void run());
  row.append(retry);
  if (opts.secondary) {
    const sec = document.createElement("button");
    sec.type = "button";
    sec.textContent = opts.secondary.label;
    sec.style.cssText = "padding:12px;border:1px solid #4a5568;border-radius:8px;background:transparent;color:#e2e8f0;font-size:15px";
    sec.addEventListener("click", () => opts.secondary?.run());
    row.append(sec);
  }
  card.append(h, p, status, row);
  overlay.append(card);
  document.body.append(overlay);
  current = overlay;
  retry.focus();
  const onOnline = () => {
    if (current !== overlay) {
      window.removeEventListener("online", onOnline);
      return;
    }
    void run();
  };
  window.addEventListener("online", onOnline);
}
